"use client";

import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText, Home, User, Menu, X, PenTool, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import NotificationBell from "@/components/NotificationBell";

// Docs app URL - configured via environment variable
const DOCS_APP_URL = import.meta.env.VITE_DOCS_APP_URL || "http://localhost:3000";

const Navbar = () => {
  const { user, isVerified, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActiveLink = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Dynamic home URL based on auth status
  const homeUrl = user ? "/feed" : "/";

  const handleCreateDoc = () => {
    // Check if user is verified before allowing doc creation
    if (!isVerified) {
      // Redirect unverified users to employment verification
      navigate("/employment-verification");
      return;
    }
    // Open docs app home page in a new tab where user can select a template
    // Cookies are shared across subdomains for seamless auth
    window.open(DOCS_APP_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/98 backdrop-blur-xl border-b border-gray-200 shadow-lg"
            : "bg-white/95 backdrop-blur-md border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link
              to={homeUrl}
              className="flex items-center space-x-3 group transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="relative">
                <div className="p-3 bg-[#CA8A04] rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <FileText className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900 tracking-tight">
                  TappAnon
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center space-x-2">
              <Link
                to={homeUrl}
                className={`group relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveLink(homeUrl)
                    ? "text-[#CA8A04] bg-[#CA8A04]/10 shadow-sm"
                    : "text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5"
                }`}
              >
                {isActiveLink(homeUrl) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-[#CA8A04] rounded-full"></div>
                )}
                <div className="flex items-center space-x-2">
                  <Home className="h-4 w-4" />
                  <span>Home</span>
                </div>
              </Link>

              {/* Browse Docs - visible to all */}
              <Link
                to="/docs"
                className={`group relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  isActiveLink("/docs")
                    ? "text-[#CA8A04] bg-[#CA8A04]/10 shadow-sm"
                    : "text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5"
                }`}
              >
                {isActiveLink("/docs") && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-[#CA8A04] rounded-full"></div>
                )}
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-4 w-4" />
                  <span>Browse Docs</span>
                </div>
              </Link>

              {/* My Documents - only for verified users */}
              {user && isVerified && (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = DOCS_APP_URL;
                  }}
                  className={`group relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5`}
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span>My Documents</span>
                  </div>
                </Link>
              )}
            </div>

            <div className="hidden md:flex items-center space-x-3">
              {user ? (
                <>
                  {/* Notification bell - all logged-in users */}
                  <NotificationBell />
                  {/* Create Doc button - visible to all logged-in users */}
                  <Button
                    onClick={handleCreateDoc}
                    className="bg-[#CA8A04] hover:bg-[#B8860B] text-white font-medium transition-all duration-300 rounded-xl px-4 py-2"
                  >
                    <PenTool className="h-4 w-4 mr-2" />
                    Create Doc
                  </Button>
                  <Link to="/profile">
                    <Button
                      variant="ghost"
                      className={`text-sm font-medium transition-all duration-300 rounded-xl px-4 py-2 ${
                        isActiveLink("/profile")
                          ? "text-[#CA8A04] bg-[#CA8A04]/10"
                          : "text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5"
                      }`}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    className="text-sm font-medium border-gray-300 text-gray-700 hover:border-[#CA8A04] hover:bg-[#CA8A04]/8 hover:text-[#CA8A04] transition-all duration-300 rounded-xl px-4 py-2 bg-transparent"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="text-sm font-medium text-gray-700 hover:text-[#CA8A04] hover:bg-[#CA8A04]/5 transition-all duration-300 rounded-xl px-4 py-2"
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      className="bg-[#CA8A04] hover:bg-[#B8860B] text-white font-medium transition-all duration-300 rounded-xl px-4 py-2 text-sm"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>

            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleMobileMenu}
                className="p-2.5 hover:bg-[#CA8A04]/8 transition-all duration-300 rounded-xl"
              >
                <Menu className="h-5 w-5 text-gray-700" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-black/20 transition-opacity duration-300"
            onClick={toggleMobileMenu}
          />
          <div className="fixed top-20 left-0 right-0 bg-white border-b border-gray-100 shadow-lg animate-in slide-in-from-top-2 duration-300">
            <div className="max-w-7xl mx-auto px-6 py-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Menu</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMobileMenu}
                  className="p-2 hover:bg-[#CA8A04]/8 transition-all duration-300 rounded-xl"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </Button>
              </div>
              <div className="flex flex-col space-y-3">
                <Link
                  to={homeUrl}
                  onClick={toggleMobileMenu}
                  className={`group flex items-center space-x-4 p-4 rounded-2xl transition-all duration-300 ${
                    isActiveLink(homeUrl)
                      ? "bg-[#CA8A04]/10 border border-[#CA8A04]/20"
                      : "hover:bg-[#CA8A04]/5"
                  }`}
                >
                  <div
                    className={`p-2.5 rounded-xl transition-all duration-300 ${
                      isActiveLink(homeUrl)
                        ? "bg-[#CA8A04]/20"
                        : "bg-[#CA8A04]/10 group-hover:bg-[#CA8A04]/20"
                    }`}
                  >
                    <Home className="h-5 w-5 text-[#CA8A04]" />
                  </div>
                  <span
                    className={`font-medium transition-colors duration-300 ${
                      isActiveLink(homeUrl)
                        ? "text-[#CA8A04]"
                        : "text-gray-900 group-hover:text-[#CA8A04]"
                    }`}
                  >
                    Home
                  </span>
                </Link>

                {/* Browse Docs - mobile */}
                <Link
                  to="/docs"
                  onClick={toggleMobileMenu}
                  className={`group flex items-center space-x-4 p-4 rounded-2xl transition-all duration-300 ${
                    isActiveLink("/docs")
                      ? "bg-[#CA8A04]/10 border border-[#CA8A04]/20"
                      : "hover:bg-[#CA8A04]/5"
                  }`}
                >
                  <div
                    className={`p-2.5 rounded-xl transition-all duration-300 ${
                      isActiveLink("/docs")
                        ? "bg-[#CA8A04]/20"
                        : "bg-[#CA8A04]/10 group-hover:bg-[#CA8A04]/20"
                    }`}
                  >
                    <BookOpen className="h-5 w-5 text-[#CA8A04]" />
                  </div>
                  <span
                    className={`font-medium transition-colors duration-300 ${
                      isActiveLink("/docs")
                        ? "text-[#CA8A04]"
                        : "text-gray-900 group-hover:text-[#CA8A04]"
                    }`}
                  >
                    Browse Docs
                  </span>
                </Link>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  {user ? (
                    <div className="flex flex-col space-y-3">
                      {/* My Documents - mobile, only for verified users */}
                      {user && isVerified && (
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = DOCS_APP_URL;
                            toggleMobileMenu();
                          }}
                          className={`group flex items-center space-x-4 p-4 rounded-2xl transition-all duration-300 hover:bg-[#CA8A04]/5`}
                        >
                          <div className={`p-2.5 rounded-xl transition-all duration-300 bg-[#CA8A04]/10 group-hover:bg-[#CA8A04]/20`}>
                            <FileText className="h-5 w-5 text-[#CA8A04]" />
                          </div>
                          <span className={`font-medium transition-colors duration-300 text-gray-900 group-hover:text-[#CA8A04]`}>
                            My Documents
                          </span>
                        </Link>
                      )}
                      {/* Notifications - mobile */}
                      <div className="flex items-center space-x-4 p-4">
                        <NotificationBell />
                      </div>

                      {/* Create Doc button - mobile, visible to all logged-in users */}
                      <Button
                        onClick={() => {
                          handleCreateDoc();
                          toggleMobileMenu();
                        }}
                        className="w-full bg-[#CA8A04] hover:bg-[#B8860B] text-white font-medium rounded-2xl p-4 transition-all duration-300"
                      >
                        <PenTool className="h-5 w-5 mr-3" />
                        Create Doc
                      </Button>
                      <Link to="/profile" onClick={toggleMobileMenu}>
                        <Button
                          variant="ghost"
                          className={`w-full justify-start text-left font-medium rounded-2xl p-4 transition-all duration-300 ${
                            isActiveLink("/profile")
                              ? "bg-[#CA8A04]/10 text-[#CA8A04]"
                              : "hover:bg-[#CA8A04]/5 hover:text-[#CA8A04]"
                          }`}
                        >
                          <User className="h-5 w-5 mr-3" />
                          Profile
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleLogout();
                          toggleMobileMenu();
                        }}
                        className="w-full font-medium border-gray-300 hover:border-[#CA8A04] hover:bg-[#CA8A04]/8 hover:text-[#CA8A04] rounded-2xl p-4 transition-all duration-300"
                      >
                        Logout
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-3">
                      <Link to="/login" onClick={toggleMobileMenu}>
                        <Button
                          variant="ghost"
                          className="w-full font-medium hover:bg-[#CA8A04]/5 hover:text-[#CA8A04] rounded-2xl p-4 transition-all duration-300"
                        >
                          Login
                        </Button>
                      </Link>
                      <Link to="/register" onClick={toggleMobileMenu}>
                        <Button
                          className="w-full bg-[#CA8A04] hover:bg-[#B8860B] text-white font-medium rounded-2xl p-4 transition-all duration-300"
                        >
                          Sign Up
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
